// Enum representing different GSM connection types
export enum GsmConnectionType {
  TWO_G = '2',
  THREE_G = '3',
  FOUR_G = '4',
}

// Status interface combining connection type name, quality key, icon path and last update time
export interface ConnectionStatus {
  connectionTypeName: string;
  signalQuality: string;
  signalQualityIcon: string;
  lastUpdateTime: string;
}

// Map defining the label for each signal quality level
export interface SignalQualityMap {
  [key: number]: string; // e.g., {0: 'critical', 1: 'low', 2: 'normal', 3: 'good', 4: 'noSignal'}
}

// A single threshold rule
export interface ThresholdRule {
  operator: '<=' | '>=' | '<' | '>' | '===';
  value: number;
  mapIndex: number; // Which signalQualityMap index should be used if matched
}

// Threshold definitions per GSM type
export interface SignalThresholds {
  [key: string]: ThresholdRule[];
}

// Utility class for GSM connection status and signal quality calculations
export class ConnectionUtil {

  /**
   * Returns full connection status (type, quality, icon, timestamp).
   */
  public static getConnectionStatus(
    gsmType: GsmConnectionType,
    gsmSignalQuality: number,
    observedAt: string,
    signalQualityMap: SignalQualityMap,
    thresholds: SignalThresholds
  ): ConnectionStatus {
    const connectionTypeName = this.getConnectionTypeName(gsmType);
    const signalQuality = this.calculateSignalQuality(gsmSignalQuality, gsmType, signalQualityMap, thresholds);

    return {
      connectionTypeName,
      signalQuality,
      signalQualityIcon: `assets/images/${signalQuality}.png`,
      lastUpdateTime: this.formatTimestamp(observedAt),
    };
  }

  /**
   * Maps GSM type enum to human-readable name.
   */
  private static getConnectionTypeName(gsmType: GsmConnectionType): string {
    switch (gsmType) {
      case GsmConnectionType.TWO_G:
        return '2G';
      case GsmConnectionType.THREE_G:
        return '3G';
      case GsmConnectionType.FOUR_G:
        return 'LTE';
      default:
        return '';
    }
  }

  /**
   * Calculates signal quality string based on thresholds and signal value.
   */
  private static calculateSignalQuality(
    signalValue: number,
    gsmType: GsmConnectionType,
    signalQualityMap: SignalQualityMap,
    thresholds: SignalThresholds
  ): string {
    const rules = thresholds[gsmType] || [];
    if (!rules.length) return signalQualityMap[4]; // fallback: no signal

    for (const rule of rules) {
      if (this.evaluateCondition(signalValue, rule.operator, rule.value)) {
        return signalQualityMap[rule.mapIndex] || signalQualityMap[4];
      }
    }

    return signalQualityMap[4];
  }

  /**
   * Evaluates a condition like <=, >=, <, >, ===
   */
  private static evaluateCondition(
    signalValue: number,
    operator: ThresholdRule['operator'],
    threshold: number
  ): boolean {
    switch (operator) {
      case '<=':
        return signalValue <= threshold;
      case '>=':
        return signalValue >= threshold;
      case '<':
        return signalValue < threshold;
      case '>':
        return signalValue > threshold;
      case '===':
        return signalValue === threshold;
      default:
        return false;
    }
  }

  /**
   * Formats a timestamp into DD.MM.YYYY HH:mm:ss
   */
  private static formatTimestamp(timestamp: string | number | Date): string {
    const date = new Date(timestamp);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  }
}
